//@ts-nocheck
import React, { useState, useRef, useEffect } from "react";
import SwitchButton from "./SwitchButton";
import Modal from "./Modal";
import SettingIcon from "../Icons/SettingsIcon";
import { tw } from "@careinfo/tailwind";
import useGetHook from "../customHooks/useGetHook";
import APIS from "../constant/Endpoint";
import usePatchHook from "../customHooks/usePatchHook";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { SortableColumnItem } from "../Reusable/Sortable/Sortable";

interface TableColumn {
  accessorKey: string;
  header: string;
  cell?: (props: any) => React.ReactNode;
}

interface TableSettingsResponse {
  page_limit: number;
  visible_columns?: string[];
}

interface TableSettingLayoutProps {
  tableName: string;
  defaultColumns: TableColumn[];
  isOpenTableSetting: boolean;
  setIsOpenTableSettings: (open: boolean) => void;
  onPageLimitChange?: (limit: number) => void;
  onSettingsLoaded?: (settings: TableSettingsResponse) => void;
}

const TableSettingLayout: React.FC<TableSettingLayoutProps> = ({
  tableName,
  defaultColumns,
  isOpenTableSetting,
  setIsOpenTableSettings,
  onSettingsLoaded,
  onPageLimitChange,
}) => {
  const { data: tableSettings } = useGetHook<TableSettingsResponse>({
    queryKey: [tableName],
    url: `${APIS.TABLE_SETTINGS}${tableName}/`,
    params: {},
  });
  const defaultVisible = Object.fromEntries(
    defaultColumns
      .filter((col) => !!col?.accessorKey)
      .map((col) => [col?.accessorKey, true])
  );

  const [columnsState, setColumnsState] = useState<Record<string, boolean>>(
    tableSettings?.visible_columns
      ? Object.fromEntries(
          defaultColumns
            .filter((col) => !!col?.accessorKey)
            .map((col) => [
              col?.accessorKey,
              tableSettings?.visible_columns.includes(col?.accessorKey),
            ])
        )
      : defaultVisible
  );

  const [columnOrder, setColumnOrder] = useState(
    defaultColumns
      .map((c) => c?.accessorKey)
      .filter((key) => typeof key === "string" && key.trim() !== "")
  );

  useEffect(() => {
    if (tableSettings?.visible_columns) {
      setColumnOrder(
        (tableSettings.visible_columns || []).filter(
          (key) => typeof key === "string" && key.trim() !== ""
        )
      );
    }
  }, [tableSettings]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  useEffect(() => {
    if (tableSettings) {
      onSettingsLoaded?.(tableSettings);
    }
  }, [tableSettings]);

  const [tempColumnsState, setTempColumnsState] =
    useState<Record<string, boolean>>(columnsState);

  const [pageLimit, setPageLimit] = useState(tableSettings?.page_limit ?? 10);
  const [tempPageLimit, setTempPageLimit] = useState(pageLimit);

  useEffect(() => {
    if (tableSettings) {
      setPageLimit(tableSettings.page_limit);
      setTempPageLimit(tableSettings.page_limit); // key line
    }
  }, [tableSettings]);

  const { mutateAsync: updateTableSettings } = usePatchHook({
    queryKey: [tableName],
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const openSettings = () => {
    setTempColumnsState({ ...columnsState });
    setTempPageLimit(pageLimit);
    setColumnOrder([...columnOrder]);
    setIsOpenTableSettings(true);
  };

  const toggleTempColumn = (key: string) => {
    setTempColumnsState((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const applyChanges = async () => {
    const updatedVisibleColumns = columnOrder.filter(
      (key) => tempColumnsState[key]
    );
    try {
      await updateTableSettings({
        url: `${APIS.TABLE_SETTINGS}${tableName}/`,
        formData: {
          page_limit: tempPageLimit,
          visible_columns: updatedVisibleColumns, // sorted order!
        },
      });
      setColumnsState(tempColumnsState);
      setPageLimit(tempPageLimit);
      setIsOpenTableSettings(false);
      onPageLimitChange?.(tempPageLimit);
    } catch (err) {
      console.error("Failed to update table settings", err);
    }
  };

  const safeCell =
    (originalCell?: (props: any) => React.ReactNode) => (props: any) => {
      const val = props.getValue?.();
      if (Array.isArray(val)) return val.join(", ");
      if (typeof val === "object" && val !== null)
        return <span>{JSON.stringify(val)}</span>;

      return originalCell ? originalCell(props) : val ?? "";
    };

  const visibleColumns = defaultColumns
    .filter((col) => columnsState[col.accessorKey])
    .map((col) => ({
      ...col,
      cell: safeCell(col.cell),
    }));

  return (
    <div className={tw`relative`} ref={containerRef}>
      <button
        aria-label="Settings"
        onClick={openSettings}
        className={tw` bg-white border border-gray-300 rounded-full p-1 shadow hover:bg-gray-100 z-50 focus:outline-none`}
      >
        <SettingIcon height="15px" width="15px" color="black" />
      </button>

      <Modal
        isOpen={isOpenTableSetting}
        setIsOpen={setIsOpenTableSettings}
        title="Table Settings"
      >
        <div className={tw`grid grid-cols-2 gap-6 mt-4`}>
          <div>
            <h4 className={tw`font-semibold mb-4 text-gray-800`}>Page size</h4>
            {[10, 50, 100, 200, 500].map((num) => (
              <label key={num} className={tw`flex items-center gap-2`}>
                <input
                  type="radio"
                  name="pageSize"
                  checked={tempPageLimit === num}
                  onChange={() => setTempPageLimit(num)}
                  className={tw`accent-primary h-4 w-4`}
                />
                <span className={tw`text-gray-700 text-sm`}>{num} items</span>
              </label>
            ))}
          </div>

          <div>
            <h4 className={tw`font-semibold mb-4 text-gray-800`}>
              Visible Columns
            </h4>

            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={columnOrder}
                strategy={verticalListSortingStrategy}
              >
                <div className={tw`space-y-2`}>
                  {columnOrder.map((key) => {
                    const col = defaultColumns.find(
                      (c) => c.accessorKey === key
                    );
                    if (!col) return null;
                    return (
                      <SortableColumnItem
                        key={key}
                        col={col}
                        enabled={!!tempColumnsState[key]}
                        onToggle={toggleTempColumn}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        <div className={tw`flex justify-end gap-2 mt-6`}>
          <button
            className={tw`px-4 py-2 bg-gray-200 rounded hover:bg-gray-300`}
            onClick={() => setIsOpenTableSettings(false)}
          >
            Cancel
          </button>
          <button
            className={tw`px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark`}
            onClick={applyChanges}
          >
            Confirm
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default TableSettingLayout;
